import { Database } from "./lib/supabase/database.types";
import {
  PHASE_DEVELOPMENT_SERVER,
  PHASE_PRODUCTION_SERVER,
  PHASE_PRODUCTION_BUILD,
  PHASE_TEST,
  PHASE_INFO,
  PHASE_EXPORT,
} from "next/constants";
import { PDLCompanySizes } from "@/lib/connection-strength/constants";
import { SupabaseClient } from "@supabase/supabase-js";

export type PDLCompanyData = {
  xid: string; // pdl id
  name: string;
  linkedInUrl: string;
  industry: string;
  size: PDLCompanySizes;
  website: string;
  region: string;
  country: string;
  locality: string;
};

export type PDLExperience = {
  linkedinUrl: string;
  title: string;
  department?: string | null;
  role: string | null;
  subRole: string | null;
  levels: string[] | null;
  startDate: string;
  endDate: string;
  company: PDLCompanyData;
};

export type PDLPrimaryExperience = PDLCompanyData & {
  linkedinUrl: string;
  title: string;
  department?: string | null;
  role: string | null;
  subRole: string | null;
  seniority: string | null;
};

// Renamed type to PDLConnection to avoid confusion with the DgraphConnection type as we're not using Dgraph anymore
export type PDLConnection = {
  uid: string;
  xid?: string;
  firstName: string | null;
  lastName: string | null;
  linkedinUrl: string | null;
  primaryExperience?: PDLPrimaryExperience[];
  experiences: PDLExperience[];
};

export type MinimalPerson = Pick<
  People,
  "firstName" | "lastName" | "company" | "companyWebsite" | "avatar"
> & {
  title?: string;
};

export type MinimalCompany = { name: string; website: string | null };

export type Notification = {
  id: string;
  requester: MinimalPerson & { id?: string };
  match: MinimalPerson;
  target: MinimalPerson;
  company: MinimalCompany;
  status: "pending" | "failed" | "success";
  pendingApproval?: boolean;
  approverId?: string | null;
  message?: string;
  subject?: string;
  connectorEmail?: string;
  requesterEmail?: string;
  ghostWriteAsUserId?: string | null;
  ghostWriterEmail?: string | null;
  ghostWriterName?: string | null;
  originalRequester?: IntroRequester | null;
  workspaceId?: string;
  connectorGroupId?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type PeopleConnection = ExtendedNetworkContact & {
  connectionStrength: number;
  confidenceScore: number;
  scoreDetails: ScoreDetails[];
};

export type People = {
  id: string;
  pdlId: string;
  avatar: string | null;
  firstName: string;
  lastName: string;
  locationName?: string;
  title: string;
  company: string;
  companyWebsite: string | null;
  companyLinkedInUrl: string;
  connectionName: string;
  connectionStrength: number;
  confidenceScore: number;
  scoreDetails: ScoreDetails[];
  seniority?: string;
  email?: string;
  connections?: PeopleConnection[];
  introRequested?: boolean;
  pendingApproval?: boolean;
  isInStarredList?: boolean;
  inLists: string[];
  linkedinUrl: string;
};

export type Experience = {
  title: string;
  role: string | null;
  subRole: string | null;
  levels: string[] | null;
  startDate: string;
  endDate: string;
  department: string;
  company: {
    name: string;
    size: string;
  };
};

// placeholder type for invitation
export type Invitation = {
  id: string;
  email: string;
  workspace: Pick<Workspace, "id" | "name" | "avatarUrl" | "slug">;
  tokenUsed: string;
  existingUser: boolean;
  initiatorName: string;
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  secondaryEmail: string | null;
  avatar: string | null;
  isPrimaryEmailVerified: boolean;
  isSecondaryEmailVerified: boolean;
  loginServices: {
    apple: boolean;
    google: boolean;
  };
};

// this will need to be addressed when we flesh the user types out
export type Workspace = {
  id: string;
  name: string;
  avatarUrl: string | null;
  slug: string;
  disabledAt: string | null;
  companyUrl: string | null;
  networkSourcedLeadsEnabled: boolean;
};

export type Persona = {
  id: string;
  name: string;
  summary: string;
  seniorities: string[];
  departments: string[];
  roles: string[];
};

export type UserWorkspace = Workspace & {
  role: WorkspaceRole;
  leftAt: string | null;
  isCurrent: boolean;
};

export type WorkspaceOrigin = "inner" | "extended" | "workspace";

export type WorkspaceUser<T> = {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  email: string;
  role: T;
  connectionCount: number;
  linkedIntegrationAccounts: {
    id: string;
    type: string;
  }[];
};

export type ExtendedNetworkContact = {
  id: string;
  pdlId: string;
  firstName: string;
  lastName: string;
  avatar?: string | null;
  relationship?: string;
  workspaceId: string;
  connectionCount: number | null;
  linkedInUrl: string;
  email?: string;
  connectorGroupId?: string | null;
  lastCrawledAt?: string | null;
  title?: string;
  company?: string;
  seniority?: string;
  companyWebsite?: string | null;
};

export type GeneralWorkspaceUser = WorkspaceUser<WorkspaceUserRole>;
export type InnerNetworkUser = WorkspaceUser<InnerNetworkRole>;

export type WorkspaceUserRole = WorkspaceRole | "invited";
export type InnerNetworkRole = "investor" | "advisor" | "invited";
export type ExtendedNetworkRole = "europe" | "sales";

export type EmailType = "magic-link" | "otp";

export type IntroStatus = (typeof IntroStatus)[keyof typeof IntroStatus];

export const IntroStatus = {
  pending: "pending",
  rejected: "rejected",
  accepted: "accepted",
} as const;

export type IntroRequester = Pick<User, "id" | "firstName" | "lastName" | "email" | "avatar">;
// TODO: eventually this should support inner or extended network contacts
export type IntroConnector = Pick<
  ExtendedNetworkContact,
  "id" | "firstName" | "lastName" | "email" | "avatar" | "relationship"
> & {
  connectorGroupId?: string | null;
};
export type IntroTarget = Pick<
  People,
  | "id"
  | "firstName"
  | "lastName"
  | "avatar"
  | "title"
  | "company"
  | "companyWebsite"
  | "linkedinUrl"
>;

export type Intro = {
  id: string;
  workspaceId: string;
  status: IntroStatus;
  createdAt: string;
  updatedAt: string;
  requester: IntroRequester;
  connector: IntroConnector;
  target: IntroTarget;
  message: string;
  subject?: string;
  pendingApproval?: boolean;
  originalRequester?: IntroRequester | null;
  approverId?: string | null;
  ghostWriteAsUserId?: string | null;
  ghostWriterEmail?: string | null;
  ghostWriterName?: string | null;
  rejectionReason?: string | null;
  hasGmailConnection?: boolean;
};

export type IntroTemplate = {
  id: string;
  name: string;
  subject: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  content: string;
};

export enum ReconnectCadence {
  Disabled = "disabled",
  Weekly = "weekly",
  BiWeekly = "bi-weekly",
  Monthly = "monthly",
  Quarterly = "quarterly",
  Yearly = "yearly",
}

export type WorkspaceRole = Database["public"]["Enums"]["workspace_role"];

export const WorkspaceRoles = {
  Member: "member" as WorkspaceRole,
  Manager: "manager" as WorkspaceRole,
  Admin: "admin" as WorkspaceRole,
} as const;

export type ScoreDetails = {
  company: string;
  sizeTier: string;
  durationAlignment: {
    label: string;
    score: number;
  };
  recencyAlignment: {
    label: string;
    score: number;
  };
  roleAlignment: {
    label: string;
    score: number;
  };
  seniorityAlignment: {
    label: string;
    score: number;
  };
  totalScore: number;
  totalScoreWithoutRecency: number;
  confidenceScore: number;
  overlapMonths: number;
  yearsOverlap: number;
  yearsAgo: number;
  // Additional context for richer insights
  overlapStartDate: string;
  overlapEndDate: string;
  companySize: number;
  roleA: string;
  roleB: string;
  subRoleA: string | null;
  subRoleB: string | null;
  seniorityA: string | null;
  seniorityB: string | null;
  titleA: string;
  titleB: string;
};

export type SyncStatus = Database["public"]["Enums"]["sync_status"];

export type List = {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  createdBy: { id: string; name: string };
  workspaceId: string | null;
  isAutomated: boolean;
  visibility: ListVisibility;
  members: { id: string; fullName: string; avatarUrl: string }[];
  crmProvider: "hubspot" | "salesforce" | null;
  externalListId: string | null;
  personaId: string | null;
  syncStatus: SyncStatus;
};

export type ListVisibility = "private" | "workspace" | "restricted";
export type ListType = "automated" | "manual";

export const SystemRoles = {
  Super: "super",
  User: "user",
} as const;

export type SystemRole = (typeof SystemRoles)[keyof typeof SystemRoles];

export type StorageBucket = "avatars" | "workspace-logos";

export type NextPhase =
  | typeof PHASE_DEVELOPMENT_SERVER
  | typeof PHASE_PRODUCTION_SERVER
  | typeof PHASE_PRODUCTION_BUILD
  | typeof PHASE_TEST
  | typeof PHASE_INFO
  | typeof PHASE_EXPORT;

export interface CSVRow {
  [key: string]: string;
}

export interface FilterRule {
  field: string;
  operator: "contains" | "is" | "is_not" | "in" | "not_in" | "gte" | "lte";
  value: string | number | (string | number)[];
  columns?: string[];
}

export interface TableOptions {
  filters?: FilterRule[];
  searchQuery?: string;
  sort?: {
    column: string;
    direction: "asc" | "desc";
  };
  page?: number;
  pageSize?: number;
}

export interface WhereClauseValue {
  eq?: string | number;
  neq?: string | number;
  isNot?: null;
}

export type CrmListResponse = {
  companies: CrmList[];
  contacts: CrmList[];
};

export type CrmProvider = "hubspot" | "salesforce";

type CrmList = {
  id: string;
  name: string;
};

export type CrmCompany = {
  name: string;
  domain: string;
  externalId: string;
  externalListId: string | null;
  id: string;
  provider: CrmProvider;
  lastSyncedAt: string | null;
  workspaceId: string;
};

export type Referrals = {
  id: string;
  firstName: string;
  lastName: string;
  companyUrl: string;
  referrerFirstName: string;
  referrerLastName: string;
  referrerEmail: string;
  referralCompanyName: string;
  connectionStrength: number;
  scoreDetails: ScoreDetails[];
  title: string;
  avatar: string | null;
  referralEmail: string;
  referralLinkedInUrl: string;
  workspaceId: string;
};

export type ReferralIntroboardSettings = {
  id: string;
  published: boolean;
  title?: string;
  subtitle?: string;
  backgroundColor: string;
  avatarUrl?: string;
  companyUrl?: string | null;
  workspaceId: string;
  targetListId: string | null;
  customReferral: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  persona?: Pick<Persona, "name" | "roles" | "departments" | "seniorities">;
  companies?: Array<{
    id: string;
    name: string;
    website: string | null;
  }>;
};

export type ReferralStylingSettings = {
  title: string;
  subtitle: string;
  backgroundColor: string;
};

export type WorkspaceCompanyData = {
  id: string;
  name: string | null;
  avatar_url: string | null;
  company_url: string | null;
  summary: string | null;
  product_benefits: string | null;
  differentiated_value: string | null;
  pain_points_solved: string | null;
  created_at: string;
  updated_at: string;
};

export type Approver = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  avatar: string | null;
};

export type GhostWriter = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  avatar: string | null;
  hasGmailConnection?: boolean;
};

export type ConnectorGroup = {
  id: string;
  name: string;
  description?: string | null;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  maxOutreachAttempts?: number | null;
  outreachPeriodDays: number;
  introductionApproverIds?: string[] | null;
  ghostWriteAsUserIds?: string[] | null;
  autoDisableAfterLimit: boolean;
};

export type ConnectorGroupLimit = {
  limit: number | null;
  used: number;
  remaining: number | null;
  groupName: string;
  isLimitReached: boolean;
  isAuthorized: boolean;
};

export type ConnectorGroupIntroductionUser = {
  id: string;
  connectorGroupId: string;
  userId: string;
  addedAt: string;
};

export type NetworkLeadsTableRow = {
  id: string;
  company_name: string;
  company_website: string;
  best_connection_name: string;
  best_connection_title: string;
  status: "pending" | "approved" | "rejected";
  company_size_bucket: number;
  company_industry: string;
  company_locality: string;
  company_region: string;
  assigned_to_name: string;
  assigned_to: string;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: any;
  rejected_reason: string;
  workspace_id: string;
};

export type TypedSupabaseClient = SupabaseClient<Database>;
